import { Link } from 'react-router-dom'
import { AlertTriangle, FileQuestion, RotateCcw, ServerCrash, WifiOff } from 'lucide-react'
import { Button } from '@/components/ui/Button'

export type ErrorVariant = 'network' | 'not-found' | 'server' | 'generic'

interface ErrorScreenProps {
  variant?: ErrorVariant
  title?: string
  description?: string
  onRetry?: () => void
  showHomeLink?: boolean
  className?: string
}

const VARIANT_CONFIG: Record<
  ErrorVariant,
  { icon: typeof AlertTriangle; title: string; description: string }
> = {
  network: {
    icon: WifiOff,
    title: 'Connection lost',
    description: 'Please check your internet connection and try again.',
  },
  'not-found': {
    icon: FileQuestion,
    title: 'Page not found',
    description: "The page you're looking for doesn't exist or has been moved.",
  },
  server: {
    icon: ServerCrash,
    title: 'Something went wrong',
    description: 'We hit an unexpected error. Please try again in a moment.',
  },
  generic: {
    icon: AlertTriangle,
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
  },
}

export function ErrorScreen({
  variant = 'generic',
  title,
  description,
  onRetry,
  showHomeLink = false,
  className,
}: ErrorScreenProps) {
  const config = VARIANT_CONFIG[variant]
  const Icon = config.icon
  const displayTitle = title ?? config.title
  const displayDescription = description ?? config.description

  return (
    <div
      className={`flex min-h-[400px] flex-col items-center justify-center px-4 py-16 text-center ${className ?? ''}`}
      data-testid={`error-screen-${variant}`}
    >
      <div className="mb-4 rounded-full bg-muted p-4 text-muted-foreground">
        <Icon className="h-10 w-10" aria-hidden />
      </div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">{displayTitle}</h2>
      <p className="mb-6 max-w-md text-sm text-muted-foreground">{displayDescription}</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {onRetry && (
          <Button className="w-auto gap-2" onClick={onRetry}>
            <RotateCcw className="h-4 w-4" />
            Try again
          </Button>
        )}
        {showHomeLink && (
          <Link
            to="/dashboard"
            className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Go to Dashboard
          </Link>
        )}
      </div>
    </div>
  )
}
